/**
 * useEvents / useEvent / useUpcomingFeed — queries de eventos com cache 2 min
 *
 * useEvents(filters)       → lista filtrada de eventos
 * useEvent(id)             → detalhe de evento (via eventsService / TS Supabase)
 * useEventStructure(id)    → estrutura de dias/corridas (via eventsService / TS Supabase)
 * useUpcomingFeed(gameId)  → feed do PilotConsole (mais dinâmico, 1 min)
 * useEventRegistrations(id) → inscritos no evento (via eventsService / TS Supabase)
 * useEventStandings(id)    → standings (via eventsService / TS Supabase)
 * useInvalidateEvents()    → invalida após criar/editar evento
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventsApi, EventFeedResponse } from '../lib/api';
import { eventsService } from '../services/eventsService';
import { TTL } from '../lib/queryClient';

export const EVENTS_KEY = ['events'] as const;
export const EVENT_KEY  = (id: string) => ['events', id] as const;

interface EventFilters {
  game_id?: string;
  category_id?: string;
  license_id?: string;
  event_type?: string;
  status?: string;
}

export function useEvents(filters: EventFilters = {}) {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'list', filters],
    queryFn: () => eventsApi.listAll(filters),
    staleTime: TTL.FAST,     // 1 min — progresso de etapas muda com frequência
    gcTime: TTL.SLOW,
  });
}

export function useEvent(id: string) {
  return useQuery({
    queryKey: EVENT_KEY(id),
    queryFn: () => eventsService.getById(id),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!id,
  });
}

export function useEventStructure(id: string) {
  return useQuery({
    queryKey: [...EVENT_KEY(id), 'structure'],
    queryFn: () => eventsService.getStructure(id),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!id,
  });
}

export function useUpcomingFeed(gameId?: string) {
  return useQuery<EventFeedResponse>({
    queryKey: [...EVENTS_KEY, 'upcoming-feed', gameId ?? 'all'],
    queryFn: () => eventsApi.getUpcomingFeed(gameId),
    staleTime: TTL.FAST,          // 1 min — feed muda com mais frequência
    gcTime: TTL.MODERATE,
  });
}

export function useEventRegistrations(eventId: string, includeReserves = false) {
  return useQuery({
    queryKey: [...EVENT_KEY(eventId), 'registrations', { includeReserves }],
    queryFn: () => eventsService.getRegistrations(eventId, includeReserves),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!eventId,
  });
}

export function useEventStandings(eventId: string) {
  return useQuery({
    queryKey: [...EVENT_KEY(eventId), 'standings'],
    queryFn: () => eventsService.getStandings(eventId),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!eventId,
  });
}

export function useEventServers(eventId: string) {
  return useQuery({
    queryKey: [...EVENT_KEY(eventId), 'servers'],
    queryFn: () => eventsService.getServers(eventId),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!eventId,
  });
}

export function useEventCars(eventId: string, enabled = true) {
  return useQuery({
    queryKey: [...EVENT_KEY(eventId), 'cars'],
    queryFn: () => eventsService.listCars(eventId),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!eventId && enabled,
  });
}

export function useEventCategories(eventId: string, enabled = true) {
  return useQuery({
    queryKey: [...EVENT_KEY(eventId), 'categories'],
    queryFn: () => eventsService.listCategories(eventId),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!eventId && enabled,
  });
}

export function useEventTeams(eventId: string, enabled = true) {
  return useQuery({
    queryKey: [...EVENT_KEY(eventId), 'teams'],
    queryFn: () => eventsService.listTeams(eventId),
    staleTime: TTL.MODERATE,
    gcTime: TTL.SLOW,
    enabled: !!eventId && enabled,
  });
}

export function useEventLapRecords(eventId: string, active = true) {
  return useQuery({
    queryKey: [...EVENT_KEY(eventId), 'pitlane-lap-records'],
    queryFn: () => eventsService.getLapRecordsByEvent(eventId),
    staleTime: 0,                        // Sempre considera stale -> busca sem cache estático
    gcTime: TTL.MODERATE,
    refetchInterval: active ? 15_000 : false, // Refetch automático a cada 15s quando o Pit Lane estiver ativo
    refetchOnWindowFocus: true,
    enabled: !!eventId && active,
  });
}

export function useEventsByLeague(leagueId: string) {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'league', leagueId],
    queryFn: () => eventsApi.listByLeague(leagueId),
    staleTime: TTL.SLOW,
    gcTime: TTL.SLOW * 2,
    enabled: !!leagueId,
  });
}

export function useEventCatalog(gameId?: string, categoryId?: string) {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'catalog', 'cars', gameId, categoryId],
    queryFn: () => eventsApi.catalog.getCars(gameId, categoryId),
    staleTime: TTL.STATIC,        // catálogo de carros raramente muda
    gcTime: TTL.STATIC,
  });
}

export function useCircuitsCatalog(gameId?: string) {
  return useQuery({
    queryKey: [...EVENTS_KEY, 'catalog', 'circuits', gameId],
    queryFn: () => eventsApi.catalog.getCircuits(gameId),
    staleTime: TTL.STATIC,
    gcTime: TTL.STATIC,
    enabled: !!gameId,  // só busca quando o jogo estiver selecionado
  });
}

export function useInvalidateEvents() {
  const qc = useQueryClient();
  return (id?: string) => {
    if (id) qc.invalidateQueries({ queryKey: EVENT_KEY(id) });
    qc.invalidateQueries({ queryKey: EVENTS_KEY });
  };
}


